import {
  ComparisonKpis,
  ComparisonWindow,
  TopEntity,
  TopGroup,
} from "@cryptors/api-client";
import {
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

interface Props {
  data: ComparisonKpis;
}

const fmt = (n: number | null) =>
  n === null ? "—" : new Intl.NumberFormat("fr-FR").format(n);

const VariationChip = ({ value }: { value: number | null }) => {
  if (value === null) return <span>—</span>;
  const positive = value >= 0;
  return (
    <Chip
      size="small"
      label={`${positive ? "+" : ""}${value}%`}
      color={value === 0 ? "default" : positive ? "success" : "error"}
      variant="outlined"
    />
  );
};

const IndicatorRow = ({
  label,
  week,
  month,
}: {
  label: string;
  week: ComparisonWindow;
  month: ComparisonWindow;
}) => (
  <TableRow>
    <TableCell>{label}</TableCell>
    <TableCell align="right">{fmt(week.previous)}</TableCell>
    <TableCell align="right">{fmt(week.current)}</TableCell>
    <TableCell align="right">
      <VariationChip value={week.variationPct} />
    </TableCell>
    <TableCell align="right">{fmt(month.previous)}</TableCell>
    <TableCell align="right">{fmt(month.current)}</TableCell>
    <TableCell align="right">
      <VariationChip value={month.variationPct} />
    </TableCell>
  </TableRow>
);

// One cell of the Top-5 matrix: the ranked list of elements + their score for
// a given indicator and period. "Aucune donnée" also covers an unavailable
// QRfy report (empty array).
const TopCell = ({ rows }: { rows: TopEntity[] }) =>
  rows.length === 0 ? (
    <Typography variant="body2" color="text.secondary">
      Aucune donnée
    </Typography>
  ) : (
    <Stack gap={0.5}>
      {rows.map((r) => (
        <Stack
          key={`${r.id}-${r.name}`}
          direction="row"
          justifyContent="space-between"
          gap={1}
        >
          <Typography variant="body2">
            {r.name}{" "}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              #{r.id}
            </Typography>
          </Typography>
          <Typography variant="body2">{fmt(r.captures)}</Typography>
        </Stack>
      ))}
    </Stack>
  );

const TOP_PERIODS: { label: string; pick: (d: ComparisonKpis) => TopGroup }[] =
  [
    { label: "Semaine N-1", pick: (d) => d.topWeekN1 },
    { label: "Semaine N", pick: (d) => d.topWeek },
    { label: "Mois N-1", pick: (d) => d.topMonthN1 },
    { label: "Mois N", pick: (d) => d.topMonth },
  ];

const TOP_INDICATORS: { label: string; pick: (g: TopGroup) => TopEntity[] }[] =
  [
    { label: "Top 5 QR", pick: (g) => g.qr },
    { label: "Top 5 cryptors", pick: (g) => g.cryptors },
    { label: "Top 5 captures par collection", pick: (g) => g.collections },
  ];

const TopMatrix = ({ data }: { data: ComparisonKpis }) => (
  <Paper sx={{ p: 2 }} elevation={1}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Indicateurs</TableCell>
          {TOP_PERIODS.map((p) => (
            <TableCell key={p.label}>{p.label}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {TOP_INDICATORS.map((ind) => (
          <TableRow key={ind.label} sx={{ verticalAlign: "top" }}>
            <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
              {ind.label}
            </TableCell>
            {TOP_PERIODS.map((p) => (
              <TableCell key={p.label} sx={{ minWidth: 220 }}>
                <TopCell rows={ind.pick(p.pick(data))} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

export const ComparisonSection = ({ data }: Props) => (
  <Stack gap={2}>
    <Typography variant="h6">Analyses hebdo et mensuelle</Typography>
    <Paper sx={{ p: 2 }} elevation={1}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Indicateur</TableCell>
            <TableCell align="right">Sem. N-1</TableCell>
            <TableCell align="right">Sem. N</TableCell>
            <TableCell align="right">Var.</TableCell>
            <TableCell align="right">Mois N-1</TableCell>
            <TableCell align="right">Mois N</TableCell>
            <TableCell align="right">Var.</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <IndicatorRow
            label="Scans de QR"
            week={data.week.scans}
            month={data.month.scans}
          />
          <IndicatorRow
            label="Captures"
            week={data.week.captures}
            month={data.month.captures}
          />
          <IndicatorRow
            label="Joueurs acquis"
            week={data.week.acquiredPlayers}
            month={data.month.acquiredPlayers}
          />
        </TableBody>
      </Table>
      <Stack direction="row" gap={3} mt={2} flexWrap="wrap">
        <Typography variant="body2">
          Total joueurs à date : <strong>{fmt(data.toDate.players)}</strong>
        </Typography>
        <Typography variant="body2">
          Total scans à date : <strong>{fmt(data.toDate.scans)}</strong>
        </Typography>
        <Typography variant="body2">
          Total captures à date : <strong>{fmt(data.toDate.captures)}</strong>
        </Typography>
      </Stack>
    </Paper>
    <TopMatrix data={data} />
  </Stack>
);
